import axios from 'axios';

import type { ProblemDetails } from '@/types/api.types';

/**
 * Maps a backend error `code` (`Willnq.SharedKernel.Error.Code` / `{Module}Errors.cs`) to a
 * user-facing message when the raw backend message isn't appropriate to show verbatim
 * (docs/development/FrontendGuidelines.md §9.2). Grows as new error catalogs land — an unmapped code falls
 * through to the backend's own message rather than a bare "Something went wrong".
 */
const ERROR_CODE_MESSAGES: Record<string, string> = {
  'Identity.InvalidCredentials': 'Incorrect email or password.',
  'Identity.InvalidRefreshToken': 'Your session has expired. Please sign in again.',
  'Identity.InvalidMfaCode': 'That verification code is incorrect.',
  'Identity.NotAuthenticated': 'Please sign in to continue.',
  'Identity.EmailInUse': 'An account with that email already exists.',
  'Identity.OAuthFailed': "We couldn't sign you in with that provider. Please try again.",
  'Identity.OAuthNotConfigured': 'This sign-in method is not available right now.',
  'User.AccountLocked': 'Too many failed attempts. Try again in 15 minutes.',
  // An administrative lock never expires on its own, so this must NOT suggest waiting it out.
  'User.AccountLockedByAdmin':
    'Your account has been locked. Please contact support to restore access.',
  // Names the way out. An unverified account is the common case and its next step is the verify
  // screen (the form puts a button here); a suspended one needs a person, and saying so beats
  // leaving the user to re-enter a password that was never the problem.
  'User.NotActive':
    "Your account isn't active yet. Verify your email to finish setting it up, or contact support if you think it was suspended.",
  'User.InvalidOtp': 'That verification code is incorrect.',
  'User.OtpExpired': 'That verification code has expired. Request a new one.',
  'User.EmailAlreadyVerified': 'This email is already verified. You can sign in.',
  // Raised by the re-authentication seam, so by both the MFA reset and Change Password. Names the
  // provider route, which is what such an account should be offered instead.
  'User.PasswordAuthUnavailable':
    'This account signs in via a social provider and has no password to confirm. Continue with your provider instead.',
  // The account gained a password since this screen loaded, so the form that omitted the
  // current-password field is now the wrong one.
  'Identity.CurrentPasswordRequired':
    'This account already has a WillnQ password. Reload the page and enter your current password to change it.',
  'Identity.ReauthenticationRequired':
    "We couldn't confirm it's you. Start again and verify your identity.",
  // Identity.OAuthIdentityMismatch is deliberately left unmapped: its server-side copy is written for
  // this exact case and says what to do differently, which a generic override would lose.
  'User.InvalidResetToken': 'This reset link is invalid. Request a new one.',
  'User.ResetTokenExpired': 'This reset link has expired. Request a new one.',
};

export function messageForErrorCode(code: string, fallbackMessage: string): string {
  return ERROR_CODE_MESSAGES[code] ?? fallbackMessage;
}

/**
 * User-safe fallbacks per HTTP status for failures that carry no structured ProblemDetails body
 * (gateway rejections, rate limits, proxies). Never surfaces the raw status code or exception to
 * the user; 401 copy stays aligned with the errorLink's session handling.
 */
const HTTP_STATUS_MESSAGES: Record<number, { code: string; message: string }> = {
  400: {
    code: 'Http.BadRequest',
    message: "Some of the information doesn't look right. Review it and try again.",
  },
  401: { code: 'Http.Unauthorized', message: 'Please sign in to continue.' },
  403: { code: 'Http.Forbidden', message: "You don't have permission to do that." },
  404: { code: 'Http.NotFound', message: "We couldn't find what you're looking for." },
  409: {
    code: 'Http.Conflict',
    message: 'That change conflicts with a more recent update. Refresh and try again.',
  },
  // ASP.NET's `[RequestSizeLimit]` aborts the request before the action runs, so this one never
  // carries a ProblemDetails body. Callers pre-validate against the real limit and name it (see
  // utils/fileValidation.ts); this is the backstop for a file that slipped past that check.
  413: {
    code: 'Http.PayloadTooLarge',
    message: 'That file is too large to upload. Choose a smaller one and try again.',
  },
  422: {
    code: 'Http.ValidationFailed',
    message: "Some of the information doesn't look right. Review it and try again.",
  },
  429: {
    code: 'Http.TooManyRequests',
    message: "You're doing that a little too fast. Wait a moment and try again.",
  },
};

function messageForHttpStatus(status: number): { code: string; message: string } {
  if (HTTP_STATUS_MESSAGES[status]) return HTTP_STATUS_MESSAGES[status];
  if (status >= 500) {
    return {
      code: 'Http.ServerError',
      message: 'Something went wrong on our end. Please try again shortly.',
    };
  }
  return { code: 'Unknown', message: 'Something went wrong. Please try again.' };
}

/** True when the browser reports no network connection at all. */
function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

const OFFLINE_ERROR = {
  code: 'Network.Offline',
  message: "You're offline. Check your connection and try again.",
};
const TIMEOUT_ERROR = {
  code: 'Network.Timeout',
  message: 'The request timed out. Please try again.',
};
const UNREACHABLE_ERROR = {
  code: 'Network.Unreachable',
  message: 'Unable to reach Willnq. Check your connection and try again.',
};

/** Extracts a display-ready message from any REST (axios) failure. */
export function parseRestError(error: unknown): { code: string; message: string } {
  if (axios.isAxiosError<ProblemDetails>(error)) {
    const problem = error.response?.data;
    if (problem?.title) {
      return {
        code: problem.title,
        message: messageForErrorCode(problem.title, problem.detail ?? 'Something went wrong.'),
      };
    }
    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
      return TIMEOUT_ERROR;
    }
    if (error.code === 'ERR_NETWORK') {
      return isOffline() ? OFFLINE_ERROR : UNREACHABLE_ERROR;
    }
    if (error.response) {
      return messageForHttpStatus(error.response.status);
    }
  }
  return { code: 'Unknown', message: 'Something went wrong. Please try again.' };
}

/**
 * A message for a failure that may have happened before any request was made. Provider sign-in flows
 * fail client-side in ways worth naming (a blocked popup, an unconfigured client id) and carry a message
 * saying what to do about it, whereas `parseRestError` reports "Something went wrong" for anything that
 * is not an HTTP failure. Server failures still go through `parseRestError`, so its code-to-copy mapping
 * is never bypassed.
 */
export function clientOrServerMessage(error: unknown): string {
  const parsed = parseRestError(error);
  return parsed.code === 'Unknown' && error instanceof Error && error.message
    ? error.message
    : parsed.message;
}
